/**
 * Longest error body still plausible as a human-readable message. Anything
 * longer is a document or a stack dump, not something a toast should show.
 */
export const MAX_TOASTABLE_BODY_LENGTH = 300;

/**
 * Whether a string `ErrorResponse.data` is safe to surface to the user in a
 * toast.
 *
 * A route error body is only a user-facing message when it came from our own
 * API. When a request fails at the edge instead — Cloudflare answering a
 * `.data` request with a 502 "Bad gateway" page, a proxy interstitial — the
 * body is a whole HTML document, and toasting it verbatim paints the markup
 * across the error page as escaped text. Three independent signals reject
 * those bodies:
 *  - a 5xx status, which our API never pairs with a message meant for display,
 *  - a body that opens with markup or a doctype,
 *  - a body longer than `MAX_TOASTABLE_BODY_LENGTH`.
 *
 * Status and body are passed in rather than read from an `ErrorResponse` so the
 * rule stays pure and unit-testable.
 */
export const isToastableErrorBody = (status: number, body: string): boolean => {
    if (status >= 500) {
        return false;
    }
    const trimmed = body.trim();
    if (!trimmed || trimmed.length > MAX_TOASTABLE_BODY_LENGTH) {
        return false;
    }
    return !trimmed.startsWith('<');
};
